/**
 * ChangeDeadlineDialog — 149-cw-r136 (2026-08-31).
 *
 * Dialog untuk mengubah deadline task dengan alasan wajib.
 * - Creator, assignee, atau user dgn tasks.update yang boleh membuka dialog ini.
 * - Alasan wajib minimal 5 karakter.
 * - Setelah submit, task otomatis reload — history tersimpan di `deadline_history[]`.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api, { formatDetail } from "@/lib/api";
import { fmtDate } from "@/lib/constants";

export default function ChangeDeadlineDialog({ open, onOpenChange, task, onChanged }) {
  const [newDeadline, setNewDeadline] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setNewDeadline(task?.deadline ? task.deadline.slice(0, 10) : "");
      setReason("");
    }
  }, [open, task]);

  const currentDateOnly = task?.deadline ? task.deadline.slice(0, 10) : "";
  const isSame = (newDeadline || "") === currentDateOnly;
  const reasonTooShort = reason.trim().length < 5;
  const disabled = busy || isSame || reasonTooShort;

  const submit = async (e) => {
    e.preventDefault();
    if (disabled) return;
    setBusy(true);
    try {
      // Convert YYYY-MM-DD → ISO string dgn 23:59:00 local biar konsisten dgn convention.
      let iso = null;
      if (newDeadline) {
        // Preserve waktu asli jika tanggal sama (jarang, karena tombol disabled)
        iso = new Date(`${newDeadline}T23:59:00`).toISOString();
      }
      await api.patch(`/tasks/${task.id}/deadline`, { new_deadline: iso, reason: reason.trim() });
      toast.success("Deadline diperbarui — assignee akan menerima notifikasi.");
      onOpenChange(false);
      onChanged?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal mengubah deadline");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-md" data-testid="change-deadline-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <CalendarClock size={18} className="text-[#0B1D3A]" /> Ubah Deadline Task
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Perubahan tanggal akan tercatat permanen di riwayat, dan assignee akan menerima notifikasi.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="bg-slate-50 border border-slate-200 p-3 text-xs" data-testid="deadline-current">
            <div className="overline text-slate-500 mb-0.5">Deadline Saat Ini</div>
            <div className="font-semibold tabular-nums text-[#0B1D3A]">
              {task?.deadline ? fmtDate(task.deadline) : <span className="italic text-slate-500">Belum diset</span>}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="overline text-slate-600">Deadline Baru <span className="text-rose-600 normal-case">*</span></Label>
            <Input
              type="date"
              value={newDeadline}
              onChange={(e) => setNewDeadline(e.target.value)}
              data-testid="new-deadline-input"
              className="rounded-none"
              required
            />
            {isSame && newDeadline && (
              <p className="text-[11px] text-amber-700">Tanggal ini sama dengan deadline saat ini — pilih tanggal berbeda.</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="overline text-slate-600">
              Alasan Perubahan <span className="text-rose-600 normal-case">*</span>
              <span className="text-slate-400 normal-case tracking-normal ml-1">(min. 5 karakter)</span>
            </Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Contoh: Menunggu approval BOD untuk material tambahan"
              rows={3}
              maxLength={500}
              data-testid="deadline-reason-input"
              className="rounded-none resize-none"
              required
            />
            <div className="flex justify-between text-[10px] text-slate-500">
              <span>{reasonTooShort ? "Wajib diisi minimal 5 karakter" : "Alasan tersimpan di riwayat"}</span>
              <span className="tabular-nums">{reason.length}/500</span>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Batal</Button>
            <Button
              type="submit"
              data-testid="submit-change-deadline"
              disabled={disabled}
              className="rounded-none bg-[#0B1D3A] hover:bg-[#122a4d] text-white uppercase text-xs tracking-wider font-semibold disabled:opacity-50"
            >
              {busy ? "Menyimpan…" : "Simpan Perubahan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
